import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../lib/api'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { useToast } from '../ui/Toast'
import '../../styles/chat-theme.css'

interface ChatStarter {
  id: string
  icon: string
  label: string
  opening_message: string
}

interface ChatMessage {
  id: string
  sender_type: 'client' | 'user' | 'ai' | 'system'
  sender_name: string | null
  content: string
  created_at: string
  kb_articles_referenced?: string | null
  confidence_score?: number | null
}

interface ChatThread {
  id: string
  subject: string | null
  status: string
}

export function ChatWidget() {
  const { enabled: ticketsEnabled } = useFeatureFlag('tickets')
  const { enabled: aiChatEnabled } = useFeatureFlag('ai_chat')
  const { toastError, toast } = useToast()

  const [isOpen, setIsOpen] = useState(false)
  const [thread, setThread] = useState<ChatThread | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [starters, setStarters] = useState<ChatStarter[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [aiThinking, setAiThinking] = useState(false)
  const [aiConfig, setAiConfig] = useState<{ ai_name?: string } | null>(null)

  // Drag state for repositioning
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    try {
      const saved = localStorage.getItem('chat-widget-pos')
      if (saved) return JSON.parse(saved)
    } catch { /* ignore */ }
    return { x: window.innerWidth - 80, y: window.innerHeight - 80 }
  })
  const [isDragging, setIsDragging] = useState(false)
  const dragStart = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Drag handlers for floating button
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    dragStart.current = { startX: clientX, startY: clientY, posX: position.x, posY: position.y }
    setIsDragging(false)
  }, [position])

  useEffect(() => {
    if (!dragStart.current) return

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!dragStart.current) return
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
      const dx = clientX - dragStart.current.startX
      const dy = clientY - dragStart.current.startY
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        setIsDragging(true)
        const newX = Math.max(28, Math.min(window.innerWidth - 28, dragStart.current.posX + dx))
        const newY = Math.max(28, Math.min(window.innerHeight - 28, dragStart.current.posY + dy))
        setPosition({ x: newX, y: newY })
      }
    }

    const handleEnd = () => {
      if (dragStart.current) {
        try { localStorage.setItem('chat-widget-pos', JSON.stringify(position)) } catch { /* ignore */ }
      }
      dragStart.current = null
    }

    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleEnd)
    window.addEventListener('touchmove', handleMove)
    window.addEventListener('touchend', handleEnd)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleEnd)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleEnd)
    }
  })

  // Load starters and AI config
  useEffect(() => {
    if (!isOpen || (!ticketsEnabled && !aiChatEnabled)) return
    const load = async () => {
      try {
        const [starterData, configData] = await Promise.all([
          api.get<ChatStarter[]>('/chat/starters'),
          api.get<{ ai_name?: string }>('/ai-config/'),
        ])
        setStarters(starterData)
        setAiConfig(configData)
      } catch {
        // Non-critical, starters are optional
      }
    }
    load()
  }, [isOpen, ticketsEnabled, aiChatEnabled])

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom()
  }, [messages, aiThinking, scrollToBottom])

  const createThread = useCallback(async (): Promise<string | null> => {
    try {
      const newThread = await api.post<ChatThread>('/chat/threads', { source: 'widget' })
      setThread(newThread)
      return newThread.id
    } catch (err) {
      toastError('Failed to start conversation', err)
      return null
    }
  }, [toastError])

  const sendMessage = useCallback(async (content: string, threadId?: string) => {
    const tid = threadId || thread?.id
    if (!tid && !threadId) {
      const newTid = await createThread()
      if (!newTid) return
      return sendMessage(content, newTid)
    }

    // Optimistic: add user message
    const tempMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      sender_type: 'client',
      sender_name: null,
      content,
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, tempMsg])
    setInput('')
    setAiThinking(true)

    try {
      const result = await api.post<{
        type: string
        message?: ChatMessage
        kbResults?: unknown[]
      }>(`/chat/threads/${tid}/messages`, { content })

      // Replace temp message + add AI response
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempMsg.id)
        const newMessages = [...filtered]

        // Add the saved client message
        if (result.message && result.type !== 'ai_response') {
          newMessages.push(result.message)
        }

        // For AI responses, the backend saves both client + AI messages
        // Re-fetch thread messages to get the full state
        return newMessages
      })

      // Reload messages from server to ensure consistency
      const threadData = await api.get<{ messages: ChatMessage[] }>(`/chat/threads/${tid}`)
      setMessages(threadData.messages || [])

      if (result.type === 'rate_limited') {
        toast('Too many messages. Please wait a moment.', 'info')
      } else if (result.type === 'quota_exceeded') {
        toast('AI chat limit reached for this month.', 'info')
      }
    } catch (err) {
      toastError('Failed to send message', err)
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id))
    } finally {
      setAiThinking(false)
    }
  }, [thread, createThread, toast, toastError])

  const handleStarterClick = async (starter: ChatStarter) => {
    setStarters([]) // Hide starters
    const threadId = await createThread()
    if (threadId) {
      await sendMessage(starter.opening_message, threadId)
    }
  }

  const handleEscalate = async () => {
    if (!thread) return
    try {
      setLoading(true)
      const result = await api.post<{ ticket_number: number }>(`/chat/threads/${thread.id}/escalate`)
      toast(`Escalated to Ticket #${result.ticket_number}`)
      // Refresh messages
      const threadData = await api.get<{ messages: ChatMessage[] }>(`/chat/threads/${thread.id}`)
      setMessages(threadData.messages || [])
    } catch (err) {
      toastError('Failed to escalate', err)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (input.trim() && !aiThinking) {
        sendMessage(input.trim())
      }
    }
  }

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 300)
  }, [])

  // Listen for global open-chat event (from QuickCreate FAB)
  useEffect(() => {
    const handler = () => handleOpen()
    window.addEventListener('open-chat-widget', handler)
    return () => window.removeEventListener('open-chat-widget', handler)
  }, [handleOpen])

  const formatTime = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const showStarters = !thread && starters.length > 0 && messages.length === 0
  const aiName = aiConfig?.ai_name || 'AI Assistant'

  // Don't render if neither feature is enabled
  if (!ticketsEnabled && !aiChatEnabled) return null

  return (
    <>
      {/* Floating button — only show when there's an active chat session */}
      {!isOpen && thread && (
        <button
          className="chat-widget-button"
          style={{ left: position.x - 28, top: position.y - 28, right: 'auto', bottom: 'auto' }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={(e) => { if (!isDragging) handleOpen(); e.preventDefault() }}
          aria-label="Open chat"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="chat-widget-panel" style={{
          left: Math.min(position.x - 420, window.innerWidth - 440),
          top: Math.max(10, position.y - 600),
          right: 'auto',
          bottom: 'auto',
        }}>
          {/* Header */}
          <div className="chat-widget-header">
            <div className="chat-widget-header-avatar">
              {aiName.charAt(0).toUpperCase()}
            </div>
            <div className="chat-widget-header-info">
              <div className="chat-widget-header-name">{aiName}</div>
              <div className="chat-widget-header-status">
                <span className="online-dot" />
                {aiChatEnabled ? 'Online' : 'Support Chat'}
              </div>
            </div>
            <button className="chat-widget-close" onClick={() => setIsOpen(false)} aria-label="Close chat">
              &times;
            </button>
          </div>

          {/* Messages / Starters */}
          <div className="chat-widget-messages">
            {showStarters ? (
              <div className="chat-starters">
                <div className="chat-starters-title">Hi there! How can we help?</div>
                <div className="chat-starters-subtitle">
                  Choose a topic or type your question below.
                </div>
                {starters.map(s => (
                  <button
                    key={s.id}
                    className="chat-starter-card"
                    onClick={() => handleStarterClick(s)}
                  >
                    <span className="chat-starter-icon">{s.icon}</span>
                    <span className="chat-starter-label">{s.label}</span>
                  </button>
                ))}
              </div>
            ) : messages.length === 0 && !thread ? (
              <div className="chat-starters">
                <div className="chat-starters-title">Hi there! How can we help?</div>
                <div className="chat-starters-subtitle">
                  Type your question below to get started.
                </div>
              </div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  className={`chat-msg chat-msg--${msg.sender_type === 'ai' ? 'ai' : msg.sender_type === 'system' ? 'system' : 'user'}`}
                >
                  <div className="chat-msg-bubble">{msg.content}</div>
                  <div className="chat-msg-meta">
                    {msg.sender_name && <span>{msg.sender_name}</span>}
                    <span>{formatTime(msg.created_at)}</span>
                  </div>
                </div>
              ))
            )}

            {aiThinking && (
              <div className="chat-msg chat-msg--ai">
                <div className="ai-thinking">
                  <span className="ai-thinking-text">AI is thinking</span>
                  <span className="ai-thinking-shimmer" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-widget-input">
            <div className="chat-widget-input-row">
              <textarea
                ref={inputRef}
                className="chat-widget-input-field"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                rows={1}
                disabled={aiThinking || loading}
              />
              <button
                className="chat-widget-send-btn"
                onClick={() => input.trim() && !aiThinking && sendMessage(input.trim())}
                disabled={!input.trim() || aiThinking || loading}
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="chat-widget-footer">
            <span>Powered by AI</span>
            {thread && thread.status !== 'escalated' && (
              <button
                className="chat-widget-footer-link"
                onClick={handleEscalate}
                disabled={loading}
              >
                Talk to a human
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}
